import { useEffect, useState } from 'react';
import { SkeletonPage } from '../components/Shimmer';
import { useNavigate, useParams } from 'react-router-dom';
import { api, articles as Api, type ArticleInput } from '@tvx/api-client';
import { Field, Row } from '../components/Field';
import { ImagePick } from '../components/ImagePick';

const BLANK: ArticleInput = {
  title: '', standfirst: '', body: '', category: 'Market News', access: 'Free', author: '',
  cover_media_id: null, meta_description: '', slug: '', status: 'draft', featured: 0, published_at: null,
};

/* News & Analysis is bought on its own, so an article is open to everyone, cut
   at the paywall break, or kept for the people who hold that access. */
const ACCESS_LABEL: Record<string, string> = {
  Free: 'Anyone',
  Partial: 'Everyone, down to the break',
  Paid: 'News & Analysis access',
};
const ACCESS_HINT: Record<string, string> = {
  Free: 'Anyone can read the whole piece.',
  Partial: 'Everything above the paywall break is public; the rest needs News access.',
  Paid: 'Only the standfirst is public. The body is withheld from the HTML.',
};

/** Roughly what a person reads in a minute, which is all this needs to be. */
const readMinutes = (body: string) => Math.max(1, Math.round(body.trim().split(/\s+/).filter(Boolean).length / 220));

export function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editingId = id && id !== 'new' ? Number(id) : null;

  const [f, setF] = useState<ArticleInput>(BLANK);
  const [meta, setMeta] = useState<{ categories: string[]; access: string[] } | null>(null);
  const [loading, setLoading] = useState(!!editingId);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [cover, setCover] = useState<string | null>(null);

  useEffect(() => { void api.get<{ categories: string[]; access: string[] }>('/meta').then(setMeta).catch(() => {}); }, []);

  useEffect(() => {
    if (!editingId) return;
    let alive = true;
    Api.get(editingId)
      .then(({ article }) => {
        if (!alive) return;
        setF({ ...BLANK, ...article });
        setCover((article as { cover_path?: string | null }).cover_path ?? null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [editingId]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = <K extends keyof ArticleInput>(k: K, v: ArticleInput[K]) => {
    setF((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  async function save(publish: boolean) {
    if (!f.title.trim()) { setError('A title is required.'); return; }
    if (publish && !f.body.trim()) { setError('There is nothing to publish — the body is empty.'); return; }
    setError('');
    setStatus(publish ? 'Publishing…' : 'Saving…');
    try {
      const body = { ...f, status: publish ? 'published' as const : 'draft' as const };
      const saved = editingId ? await Api.update(editingId, body) : await Api.create(body);
      setDirty(false);
      setStatus(publish ? 'Published — live on the site now' : 'Saved as a draft');
      if (!editingId) navigate(`/articles/${saved.article.id}`, { replace: true });
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  if (loading) return <SkeletonPage />;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 6 }}>
            Content library / {editingId ? 'Edit' : 'New article'}
          </div>
          <h1 style={{ fontSize: 29 }}>{f.title || 'Untitled'}</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {f.body.trim() ? `about ${readMinutes(f.body)} min to read` : 'nothing written yet'}
            {f.status === 'published' ? ' · published' : ' · draft'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: dirty ? 'var(--gold)' : 'var(--tx3)' }}>
            {dirty ? 'Unsaved changes' : status}
          </span>
          <button className="b bo" type="button" onClick={() => navigate('/library')}>Back</button>
          <button className="b bo" type="button" onClick={() => void save(false)}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void save(true)}>
            {f.status === 'published' ? 'Save' : 'Publish'}
          </button>
        </div>
      </div>

      {error ? <div className="err">{error}</div> : null}

      <div className="article-split" style={{ display: 'grid', gap: 18, alignItems: 'start' }}>
        <div className="crd" style={{ padding: '22px 24px' }}>
          <Field label="Headline">
            <input className="inp" value={f.title} autoFocus
              onChange={(e) => set('title', e.target.value)} /></Field>
          <Field label="Standfirst" hint="The sentence under the headline, and what a search result shows if no meta description is set.">
            <textarea className="inp ta" rows={2} value={f.standfirst}
              onChange={(e) => set('standfirst', e.target.value)} /></Field>
          <Field label="Body" hint="HTML is allowed — paragraphs, headings, lists and links.">
            <textarea className="inp ta" rows={22} style={{ lineHeight: 1.7, fontSize: 14 }}
              value={f.body} onChange={(e) => set('body', e.target.value)} /></Field>
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 12 }}>Publishing</div>
            <Row cols={2}>
              <Field label="Category">
                <select className="inp" value={f.category} onChange={(e) => set('category', e.target.value)}>
                  {(meta?.categories ?? [f.category]).map((c) => <option key={c}>{c}</option>)}
                </select></Field>
              <Field label="Who can read" hint={ACCESS_HINT[f.access] ?? ''}>
                <select className="inp" value={f.access}
                  onChange={(e) => set('access', e.target.value as ArticleInput['access'])}>
                  {(meta?.access ?? [f.access]).map((a) => <option key={a} value={a}>{ACCESS_LABEL[a] ?? a}</option>)}
                </select></Field>
            </Row>
            <Field label="Author"><input className="inp" value={f.author}
              onChange={(e) => set('author', e.target.value)} /></Field>
            <Field label="URL slug" hint="Left blank, it is made from the headline. Changing it after publishing breaks existing links.">
              <input className="inp" value={f.slug} onChange={(e) => set('slug', e.target.value)} /></Field>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.featured} onChange={(e) => set('featured', e.target.checked ? 1 : 0)} />
              <span style={{ fontSize: 13.5 }}>Feature it — only one article is featured at a time</span>
            </label>
          </div>

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 12 }}>Thumbnail</div>
            <ImagePick path={cover} alt={f.title}
              hint="Shown on the article card in the member area's reading list, and when the piece is shared. Converted and resized on upload."
              onPick={(id, p) => { set('cover_media_id', id); setCover(p); }} />
          </div>

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 12 }}>Search &amp; sharing</div>
            <Field label="Meta description" hint={`${f.meta_description.length}/155 — left blank, the standfirst is used.`}>
              <textarea className="inp ta" rows={3} value={f.meta_description}
                onChange={(e) => set('meta_description', e.target.value)} /></Field>
          </div>
        </div>
      </div>
    </div>
  );
}
